import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import { MemberIdentity } from '../members/MemberIdentity'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { cartCount, cartName, cartTotal, formatMoney } from './money'
import type { Cart, Product } from './types'

const PAYMENT_METHODS: DeskPaymentMethod[] = ['cash', 'card', 'transfer']

interface Props {
  cart: Cart
  productsById: Map<string, Product>
  currency: string
  selling: boolean
  onSetQuantity: (productId: string, quantity: number) => void
  onSell: (method: DeskPaymentMethod) => void
  onDiscard: () => void
  onOpenProfile: (userId: string) => void
  /** Moves a walk-in cart onto a member. */
  onAssign: () => void
}

/** One climber's cart: what is in it, what it comes to, and how it was paid. */
export function CartPanel({
  cart,
  productsById,
  currency,
  selling,
  onSetQuantity,
  onSell,
  onDiscard,
  onOpenProfile,
  onAssign,
}: Props) {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const [method, setMethod] = useState<DeskPaymentMethod | null>(null)

  // Chosen fresh for every cart. Carrying "card" over from the last climber is
  // how a cash sale ends up in the wrong column at the end of the day.
  useEffect(() => {
    setMethod(null)
  }, [cart.id])

  const name = cartName(cart) || t('products.guest')
  const total = cartTotal(cart, productsById)
  const count = cartCount(cart)
  const identity = (
    <MemberIdentity
      userId={cart.user_id}
      name={name}
      nickname={cart.member?.nickname}
      email={cart.member?.email}
      avatarExt={cart.member?.avatar_ext}
      avatarUpdatedAt={cart.member?.avatar_updated_at}
      primaryColor={primaryColor}
    />
  )

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        {cart.user_id ? (
          <Button
            variant="light"
            className="h-auto min-w-0 justify-start px-1 py-1 text-left"
            title={t('members.viewProfile')}
            onPress={() => onOpenProfile(cart.user_id as string)}
          >
            {identity}
          </Button>
        ) : (
          <div className="min-w-0 px-1 py-1">{identity}</div>
        )}
        <Button
          size="sm"
          variant="light"
          color="danger"
          className="shrink-0"
          isDisabled={selling}
          onPress={() => {
            // An empty cart has nothing to lose, so it closes without asking.
            if (count === 0 || window.confirm(t('products.confirmClose'))) onDiscard()
          }}
        >
          {t('products.closeCart')}
        </Button>
      </div>

      {cart.user_id === null && (
        <Button
          size="sm"
          variant="flat"
          className="-mt-2 self-start"
          isDisabled={selling}
          onPress={onAssign}
        >
          {t('products.assignToMember')}
        </Button>
      )}

      {cart.items.length === 0 ? (
        <p className="py-8 text-center text-sm text-foreground/50">{t('products.cartEmpty')}</p>
      ) : (
        <div className="flex flex-col divide-y divide-divider">
          {cart.items.map((line) => {
            const product = productsById.get(line.product_id)
            const lineName = product?.name ?? t('products.unavailable')
            const short = product !== undefined && line.quantity > product.stock
            return (
              <div key={line.product_id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{lineName}</p>
                  {product && (
                    <p className="text-xs text-foreground/50">
                      {formatMoney(product.sell_price, currency)}
                    </p>
                  )}
                  {short && (
                    <p className="text-xs text-warning-600">
                      {product.stock > 0
                        ? t('products.lowStockLine', { n: product.stock })
                        : t('products.noStockLine')}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="flat"
                    aria-label={t('products.decrease', { name: lineName })}
                    onPress={() => onSetQuantity(line.product_id, line.quantity - 1)}
                  >
                    −
                  </Button>
                  <p className="w-7 text-center text-sm font-semibold tabular-nums">
                    {line.quantity}
                  </p>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="flat"
                    aria-label={t('products.increase', { name: lineName })}
                    isDisabled={!product}
                    onPress={() => onSetQuantity(line.product_id, line.quantity + 1)}
                  >
                    +
                  </Button>
                </div>
                <p className="w-20 shrink-0 text-right text-sm font-medium tabular-nums">
                  {formatMoney(line.quantity * (product?.sell_price ?? 0), currency)}
                </p>
              </div>
            )
          })}
        </div>
      )}

      <div className="flex items-baseline justify-between border-t border-divider pt-3">
        <p className="text-sm text-foreground/60">
          {t('products.total')} · {t('products.itemCount', { count })}
        </p>
        <p className="text-xl font-bold tabular-nums">{formatMoney(total, currency)}</p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium text-foreground/60">{t('products.payWith')}</p>
        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_METHODS.map((option) => (
            <Button
              key={option}
              size="lg"
              color={method === option ? 'primary' : 'default'}
              variant={method === option ? 'solid' : 'flat'}
              aria-pressed={method === option}
              isDisabled={selling}
              className="px-1 text-sm"
              onPress={() => setMethod(option)}
            >
              {t(`invoices.${option}`)}
            </Button>
          ))}
        </div>
      </div>

      <Button
        size="lg"
        color="primary"
        className="font-semibold"
        isLoading={selling}
        isDisabled={count === 0 || method === null}
        onPress={() => method && onSell(method)}
      >
        {t('products.sellAmount', { total: formatMoney(total, currency) })}
      </Button>
      {count > 0 && method === null && (
        <p className="-mt-2 text-center text-xs text-foreground/50">
          {t('products.choosePayment')}
        </p>
      )}
    </div>
  )
}
